// src/App.jsx (replace the whole file if easier)
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './App.css';

import ErrorBanner from './Error';
import Form from './components/Form';
import ReservationList from './components/ReservationList';
import client from './api/client';
import NANA from './nn.png';

function dateOnly(d) {
	const x = new Date(d);
	x.setHours(0, 0, 0, 0);
	return x;
}

function toUTCMidnight(d) {
	return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
}

// Interpret API timestamps by their UTC calendar day so saved date-only values
// do not shift in local timezones.
function utcDateToLocalMidnight(value) {
	const d = new Date(value);
	return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function inRangeDay(date, start, end) {
	const day = dateOnly(date).getTime();
	const s = dateOnly(start).getTime();
	const e = dateOnly(end).getTime();
	return day >= s && day <= e; // [start, end] — end date is blocked too
}

function fmtRange(start, end) {
	if (!start && !end) return 'Select a start and end date';
	if (start && !end) return `Start: ${start.toLocaleDateString()}`;
	return `${start.toLocaleDateString()} → ${end.toLocaleDateString()}`;
}

function Modal({ open, title, onClose, children }) {
	useEffect(() => {
		if (!open) return;
		const onKeyDown = (e) => {
			if (e.key === 'Escape') onClose();
		};
		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, [open, onClose]);

	if (!open) return null;

	return (
		<div
			className='fixed inset-0 z-50 flex items-center justify-center p-4'
			role='dialog'
			aria-modal='true'
			aria-label={title}
		>
			<button
				type='button'
				className='absolute inset-0 bg-black/50'
				onClick={onClose}
				aria-label='Close modal'
			/>
			<div className='relative w-full max-w-lg rounded-2xl border border-slate-200 bg-white shadow-2xl'>
				<div className='flex items-center justify-between gap-3 border-b border-slate-200 px-5 py-4'>
					<div className='text-sm font-semibold text-slate-900'>{title}</div>
					<button
						type='button'
						onClick={onClose}
						className='rounded-lg px-2 py-1 text-sm text-slate-600 hover:bg-slate-100'
						aria-label='Close'
					>
						✕
					</button>
				</div>
				<div className='p-5'>{children}</div>
			</div>
		</div>
	);
}

export default function App() {
	const [range, setRange] = useState([null, null]);
	const [name, setName] = useState('');
	const [error, setError] = useState('');
	const [notice, setNotice] = useState('');
	const [calendarLoading, setCalendarLoading] = useState(false);
	const [listLoading, setListLoading] = useState(false);
	const [saving, setSaving] = useState(false);

	// Dates-only — always fetched, drives the calendar
	const [blockedDates, setBlockedDates] = useState([]);
	const [reservations, setReservations] = useState([]);

	const [isEditMode, setIsEditMode] = useState(false);

	const [calendarOpen, setCalendarOpen] = useState(false);

	const refreshEditMode = useCallback(() => {
		try {
			setIsEditMode(window.localStorage.getItem('nanashotel') === 'edit');
		} catch {
			setIsEditMode(false);
		}
	}, []);

	useEffect(() => {
		refreshEditMode();
		const onStorage = (e) => {
			if (e.key === 'nanashotel') refreshEditMode();
		};
		window.addEventListener('storage', onStorage);
		return () => window.removeEventListener('storage', onStorage);
	}, [refreshEditMode]);

	const [showPast, setShowPast] = useState(false);

	const startDate = range?.[0] ?? null;
	const endDate = range?.[1] ?? null;

	const getBlockedDates = useCallback(async () => {
		try {
			setCalendarLoading(true);
			const { data } = await client.get('/reservations/dates');
			setBlockedDates(Array.isArray(data) ? data : []);
		} catch (e) {
			setError(
				e?.response?.data?.message ||
					e?.message ||
					'Failed to load availability',
			);
		} finally {
			setCalendarLoading(false);
		}
	}, []);

	const getReservations = useCallback(async () => {
		try {
			setListLoading(true);
			const { data } = await client.get('/reservations');
			setReservations(Array.isArray(data) ? data : []);
		} catch (e) {
			setError(
				e?.response?.data?.message ||
					e?.message ||
					'Failed to load reservations',
			);
		} finally {
			setListLoading(false);
		}
	}, []);

	// Always fetch blocked dates for the calendar
	useEffect(() => {
		getBlockedDates();
	}, [getBlockedDates]);

	useEffect(() => {
		getReservations();
	}, [getReservations]);

	useEffect(() => {
		if (!error) return;
		const t = setTimeout(() => setError(''), 3500);
		return () => clearTimeout(t);
	}, [error]);

	useEffect(() => {
		if (!notice) return;
		const t = setTimeout(() => setNotice(''), 2500);
		return () => clearTimeout(t);
	}, [notice]);

	const updateReservation = useCallback(
		async (id, patch) => {
			await client.put(`/reservations/${id}`, patch);
			await Promise.all([getBlockedDates(), getReservations()]);
		},
		[getBlockedDates, getReservations],
	);

	const deleteReservation = useCallback(
		async (id) => {
			await client.delete(`/reservations/${id}`);
			await Promise.all([getBlockedDates(), getReservations()]);
		},
		[getBlockedDates, getReservations],
	);

	// Calendar uses blockedDates (no names needed)
	const reservedRanges = useMemo(() => {
		return blockedDates
			.map((r) => ({
				id: r.id || r._id,
				startDate: utcDateToLocalMidnight(r.startDate),
				endDate: utcDateToLocalMidnight(r.endDate),
			}))
			.filter(
				(r) =>
					!Number.isNaN(r.startDate.getTime()) &&
					!Number.isNaN(r.endDate.getTime()),
			);
	}, [blockedDates]);

	// List uses full reservation data (names + dates)
	const reservationRows = useMemo(() => {
		return reservations
			.map((r) => ({
				id: r.id || r._id,
				name: r.name,
				startDate: utcDateToLocalMidnight(r.startDate),
				endDate: utcDateToLocalMidnight(r.endDate),
			}))
			.filter(
				(r) =>
					!Number.isNaN(r.startDate.getTime()) &&
					!Number.isNaN(r.endDate.getTime()),
			);
	}, [reservations]);

	const nowDay = useMemo(() => dateOnly(new Date()).getTime(), []);

	const isDateReserved = useCallback(
		(date) =>
			reservedRanges.some((r) => inRangeDay(date, r.startDate, r.endDate)),
		[reservedRanges],
	);

	const isDateInPastReservation = useCallback(
		(date) => {
			return reservedRanges.some((r) => {
				const ended = dateOnly(r.endDate).getTime() < nowDay;
				return ended && inRangeDay(date, r.startDate, r.endDate);
			});
		},
		[reservedRanges, nowDay],
	);

	const tileDisabled = useCallback(
		({ date, view }) => (view === 'month' ? isDateReserved(date) : false),
		[isDateReserved],
	);

	const tileClassName = useCallback(
		({ date, view }) => {
			if (view !== 'month') return '';
			if (showPast && isDateInPastReservation(date))
				return 'rc-reserved rc-reserved--past';
			if (isDateReserved(date)) return 'rc-reserved rc-reserved--upcoming';
			return '';
		},
		[isDateReserved, isDateInPastReservation, showPast],
	);

	const handleCalendarChange = (value) => {
		if (Array.isArray(value)) setRange([value[0] ?? null, value[1] ?? null]);
		else setRange([value ?? null, null]);

		if (Array.isArray(value) && value[0] && value[1]) {
			setCalendarOpen(false);
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		const trimmed = name.trim();
		if (!trimmed) return setError('Please enter a name.');
		if (!startDate) return setError('Please choose a start date.');
		if (!endDate) return setError('Please choose an end date.');
		if (endDate < startDate)
			return setError('End date must be on or after start date.');

		try {
			setSaving(true);
			await client.post('/reservation', {
				name: trimmed,
				startDate: toUTCMidnight(startDate).toISOString(),
				endDate: toUTCMidnight(endDate).toISOString(),
			});
			setName('');
			setRange([null, null]);
			setNotice('Reservation saved');
			await getBlockedDates();
			await getReservations();
		} catch (err) {
			setError(
				err?.response?.data?.message ||
					err?.message ||
					'Failed to save reservation',
			);
		} finally {
			setSaving(false);
		}
	};

	return (
		<div className='min-h-screen bg-slate-50 text-slate-900'>
			<div
				className='min-h-screen'
				style={{
					backgroundImage: `url(${NANA})`,
					backgroundSize: 'cover',
					backgroundPosition: 'center',
					backgroundRepeat: 'no-repeat',
				}}
			>
				{/* UPDATED: darker overlay + stronger blur so text is readable on any image */}
				<div className='min-h-screen bg-black/55 backdrop-blur-sm'>
					<div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10'>
						<div className='mb-4 flex items-center gap-2'>
							{isEditMode && (
								<div
									className={
										isEditMode
											? 'rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-900'
											: 'rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-700'
									}
								>
									{isEditMode ? 'EDIT' : 'VIEW'}
								</div>
							)}
						</div>
						<div className='mt-10 grid gap-6 lg:grid-cols-10'>
							<div className='col-span-6 flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between'>
								<div className='w-full lg:max-w-xl'>
									<div className='surface-card'>
										<form onSubmit={handleSubmit}>
											<Form
												name={name}
												setName={setName}
												disabled={saving}
												helperText='Enter your name, then select a start and end date.'
												startDate={startDate}
												endDate={endDate}
												onOpenCalendar={() => setCalendarOpen(true)}
											/>

											<div className='mt-5 grid gap-4 lg:grid-cols-5'>
												<div className='lg:col-span-3'>
													<div className='text-xs text-slate-600 mb-2'>
														Selected range
													</div>
													<div className='rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800'>
														{fmtRange(startDate, endDate)}
													</div>
												</div>

												<div className='lg:col-span-2 flex items-end justify-end'>
													<button
														type='submit'
														disabled={saving}
														className='w-full rounded-xl bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-lg hover:bg-indigo-500 disabled:opacity-60 transition'
													>
														{saving ? 'Saving…' : 'Save reservation'}
													</button>
												</div>
											</div>

											{error ? (
												<div className='mt-4'>
													<ErrorBanner message={error} />
												</div>
											) : null}

											{notice ? (
												<div className='mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900'>
													{notice}
												</div>
											) : null}
										</form>
									</div>
								</div>
							</div>

							<div className='col-span-6'>
								<div className='w-full lg:max-w-xl'>
									<div className='surface-card'>
										<div className='flex items-center justify-between gap-3'>
											<div>
												<div className='text-sm font-semibold text-slate-900'>
													Reservations
												</div>
												<div className='text-xs text-slate-600'>
													{showPast
														? 'Showing past + upcoming'
														: 'Showing upcoming only'}
												</div>
											</div>

											<button
												type='button'
												onClick={() => setShowPast((v) => !v)}
												className='inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 transition'
												aria-pressed={showPast}
											>
												<span
													className={
														showPast ? 'text-indigo-700' : 'text-slate-500'
													}
												>
													{showPast ? 'Past: On' : 'Past: Off'}
												</span>
											</button>
										</div>

										<div className='mt-4'>
											<ReservationList
												loading={listLoading}
												reservations={reservationRows}
												showPast={showPast}
												isEditMode={isEditMode}
												onUpdate={updateReservation}
												onDelete={deleteReservation}
											/>
										</div>
									</div>
								</div>
							</div>
						</div>

						<div className='h-8' />
					</div>

					{/* Calendar modal */}
					<Modal
						open={calendarOpen}
						title='Choose dates'
						onClose={() => setCalendarOpen(false)}
					>
						<div className='flex items-center justify-between gap-3 mb-4'>
							<div className='text-xs text-slate-600'>
								{calendarLoading
									? 'Loading availability…'
									: 'Click a start date, then an end date'}
							</div>
							<button
								type='button'
								onClick={() => setRange([null, null])}
								className='rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 transition'
							>
								Clear
							</button>
						</div>

						<Calendar
							value={range}
							selectRange
							views={['month']}
							locale='en-US'
							calendarType='gregory'
							onChange={handleCalendarChange}
							tileDisabled={tileDisabled}
							tileClassName={tileClassName}
						/>

						<div className='mt-4 flex items-center justify-between gap-3'>
							<div className='text-sm text-slate-800'>
								{fmtRange(startDate, endDate)}
							</div>
							<button
								type='button'
								onClick={() => setCalendarOpen(false)}
								className='rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-500 disabled:opacity-60 transition'
								disabled={!startDate || !endDate}
							>
								Done
							</button>
						</div>
					</Modal>
				</div>
			</div>
		</div>
	);
}
